import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { jwtDecode } from "jwt-decode";
import { io, Socket } from "socket.io-client";
import { useQueryClient } from "@tanstack/react-query";
import {
  TokenPayload,
  NotificationContextTypeProps,
} from "../types/ComponentProps";
import NotificationModal from "../components/notificationModal/NotificationModal";

const socketURL = process.env.REACT_APP_SOCKET_BASE_URL;

const NotificationContext = createContext<
  NotificationContextTypeProps | undefined
>(undefined);

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const token = localStorage.getItem("accessTokenGC");

  if (!token) {
    throw new Error("No access token found");
  }

  const userDetails: TokenPayload = jwtDecode(token);
  const queryClient = useQueryClient();

  const [socketState, setSocketState] = useState<Socket | null>(null);
  const [notificationModal, setNotificationModal] = useState(false);
  const [selectedPostNotification, setSelectedPostNotification] =
    useState<any>(null);
  const [selectedNotificationId, setSelectedNotificationId] =
    useState<any>(null);
  const [hasNewNotification, setHasNewNotification] = useState(false);

  const clearNewNotification = () => setHasNewNotification(false);

  // UseEffects
  useEffect(() => {
    const socket = io(socketURL);
    setSocketState(socket);
    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    socketState?.emit("addUser", userDetails.id);
  }, [socketState, userDetails]);

  // Listen for any incoming notification event and set the red dot flag
  useEffect(() => {
    if (!socketState) return;

    const handleNewNotification = () => setHasNewNotification(true);
    const handleRemoveNotification = () => {
      queryClient.invalidateQueries({ queryKey: ["getNotificationById"] });
    };

    socketState.on("getNotification", handleNewNotification);
    socketState.on("getNotificationComment", handleNewNotification);
    socketState.on("getNotificationMention", handleNewNotification);
    socketState.on("getNotificationBadgeMention", handleNewNotification);
    socketState.on("getNotificationCommentReaction", handleNewNotification);
    socketState.on("removeNotification", handleRemoveNotification);

    return () => {
      socketState.off("getNotification", handleNewNotification);
      socketState.off("getNotificationComment", handleNewNotification);
      socketState.off("getNotificationMention", handleNewNotification);
      socketState.off("getNotificationBadgeMention", handleNewNotification);
      socketState.off("getNotificationCommentReaction", handleNewNotification);
      socketState.off("removeNotification", handleRemoveNotification);
    };
  }, [socketState, queryClient]);

  return (
    <NotificationContext.Provider
      value={{
        userDetails,
        socketState,
        setNotificationModal,
        setSelectedNotificationId,
        setSelectedPostNotification,
        hasNewNotification,
        clearNewNotification,
      }}
    >
      {notificationModal ? (
        <NotificationModal
          notificationModal={notificationModal}
          setNotificationModal={setNotificationModal}
          selectedPostNotification={selectedPostNotification}
          setSelectedPostNotification={setSelectedPostNotification}
          selectedNotificationId={selectedNotificationId}
          socket={socketState}
        />
      ) : null}
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider",
    );
  }
  return context;
};
